/* Product Studio callouts. Ships to the theme next to product-studio.css and product-studio-player.js; loaded once per page. Enhances every
   <div class="ps__callouts">: a legend row and its pin on the picture light up together under the pointer, with the
   spot the callout marks, and a click on either zooms the picture to that callout; the same again, the picture, or
   Escape zooms out. Without this script the picture, the pins and the legend stand as they are. The markup carries every
   number: a pin's point and a spot's area as percentages of the picture, and the picture's fill; the zoom is written back
   onto the board as three custom properties the stylesheet reads. */
(() => {
  const SHARE = 0.75;   // the box around a callout's spot fills this much of the frame's shorter side
  const CAP = 4;        // past the picture ladder's top candidate a zoom goes soft
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const num = (el, name) => +el.style.getPropertyValue(name);

  const zoomed = new WeakMap();   // board → the callout it is zoomed to
  const parts = (block) => {
    const board = block.querySelector('.ps__board');
    return { board, picture: board?.querySelector('img'), pins: [...(board?.querySelectorAll('.ps__pin') || [])], rows: [...block.querySelectorAll('.ps__legend li')] };
  };
  // the row or the board pin under an event, as its block and index
  function at(e) {
    const el = e.target.closest?.('.ps__legend li, .ps__board .ps__pin');
    if (!el) return null;
    const block = el.closest('.ps__callouts');
    if (!block) return null;
    const { pins, rows } = parts(block);
    return { block, el, i: (el.tagName === 'LI' ? rows : pins).indexOf(el) };
  }

  function light(block, i, on) {
    const { board, pins, rows } = parts(block);
    on = on || (board && zoomed.get(board) === i);
    pins[i]?.classList.toggle('is-hot', on);
    rows[i]?.querySelector('.ps__pin')?.classList.toggle('is-hot', on);
    block.querySelectorAll(`.ps__spot[data-pin="${i + 1}"]`).forEach((s) => s.classList.toggle('is-hot', on));
  }

  // The zoom: a callout with a spot zooms to it, so the spot's longer side fills three quarters of the frame, and never
  // tighter, whatever the spot's shape; a spot so large that this is under 1.1x gets no zoom, only its light. A callout
  // without one zooms 2.5x on its pin. With a fill the feature comes to the centre outright and the fill continues past
  // the picture's edge; without one the picture point at the centre is held in from the edges so the picture always
  // covers the board. The picture is asked for a candidate as wide as it is now drawn; a browser only ever steps up.
  function zoomTo(block, i) {
    const { board, picture, pins } = parts(block);
    if (!board) return;
    const was = zoomed.get(board) ?? -1;
    zoomed.set(board, i);
    if (was >= 0) light(block, was, false);
    if (i < 0) {
      board.classList.remove('is-zoomed');
      ['--ps-zoom', '--ps-zoom-x', '--ps-zoom-y'].forEach((k) => board.style.removeProperty(k));
      pins.forEach((pin) => pin.classList.remove('is-off'));
      return;
    }
    const spot = block.querySelector(`.ps__spot[data-pin="${i + 1}"]`);
    const area = spot && [spot.style.left, spot.style.top, spot.style.width, spot.style.height].map(parseFloat);
    const fit = area ? Math.min(100 / area[2], 100 / area[3]) * SHARE : 2.5;
    const zs = fit < 1.1 ? 1 : Math.min(fit, CAP);
    const cx = zs === 1 ? 50 : area ? area[0] + area[2] / 2 : num(pins[i], '--ps-x');
    const cy = zs === 1 ? 50 : area ? area[1] + area[3] / 2 : num(pins[i], '--ps-y');
    const fill = getComputedStyle(board).getPropertyValue('--ps-backdrop').trim();
    const hold = !fill || fill === 'transparent' ? 50 / zs : 0;
    const fx = clamp(cx, hold, 100 - hold), fy = clamp(cy, hold, 100 - hold);
    board.style.setProperty('--ps-zoom', zs);
    board.style.setProperty('--ps-zoom-x', fx);
    board.style.setProperty('--ps-zoom-y', fy);
    board.classList.add('is-zoomed');
    light(block, i, true);
    pins.forEach((pin) => {   // a pin the zoom carries off the board fades rather than floating over the words
      const x = num(pin, '--ps-x') * zs + 50 - fx * zs, y = num(pin, '--ps-y') * zs + 50 - fy * zs;
      pin.classList.toggle('is-off', x < 0 || x > 100 || y < 0 || y > 100);
    });
    if (picture?.sizes) picture.sizes = `${Math.round(board.clientWidth * zs)}px`;
  }

  // a touch's pointerover is the tap itself: lit on it, the veil would flash and the pin grow under the finger, which is what
  // moves iOS's tap target off the pin; the click lights what it zooms to
  document.addEventListener('pointerover', (e) => { if (e.pointerType === 'touch') return; const t = at(e); if (t && !t.el.contains(e.relatedTarget)) light(t.block, t.i, true); });
  document.addEventListener('pointerout', (e) => { if (e.pointerType === 'touch') return; const t = at(e); if (t && !t.el.contains(e.relatedTarget)) light(t.block, t.i, false); });
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;   // the dashboard makes the stage inert while editing
    const t = at(e);
    if (t) { zoomTo(t.block, zoomed.get(parts(t.block).board) === t.i ? -1 : t.i); return; }
    const view = e.target.closest?.('.ps__board.is-zoomed .ps__view');
    if (view) zoomTo(view.closest('.ps__callouts'), -1);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') document.querySelectorAll('.ps__board.is-zoomed').forEach((b) => zoomTo(b.closest('.ps__callouts'), -1));
  });

  // is-live marks a block this script has reached, which is what gives its rows and pins a pointer cursor, and each row and
  // pin gets a listener of its own: iOS takes the nearest element with one as a tap's target and snaps a tap beside it to it,
  // where one without is handed to the picture. watch() can be called again for blocks added later (the dashboard re-renders
  // its preview)
  const target = () => {};
  function watch(root = document) {
    root.querySelectorAll('.ps__callouts:not(.is-live)').forEach((el) => {
      el.classList.add('is-live');
      el.querySelectorAll('.ps__board .ps__pin, .ps__legend li').forEach((t) => t.addEventListener('click', target));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => watch()); else watch();

  window.ProductStudioCallouts = { watch, zoomTo };
})();
